<template>
  <BaseModal :model-value="modelValue" @update:model-value="onClose">
    <h2 class="title">Mon bloc-notes</h2>
    <p class="sub">Notes personnelles persistantes (privées, non liées à un appel).</p>
    <textarea
      v-model="content"
      class="pad"
      placeholder="Écrivez ce que vous voulez ici…"
      @blur="save"
    />
    <div class="foot">
      <span class="status">{{ statusText }}</span>
      <span class="spacer" />
      <button class="btn-primary" :disabled="saving" @click="save">Enregistrer</button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { NotebookAPI } from '@/shared/api/forms'
import BaseModal from '@/shared/components/BaseModal.vue'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>()

const content = ref('')
const loaded = ref(false)
const saving = ref(false)
const savedAt = ref<string | null>(null)
const dirty = ref(false)

const statusText = computed(() => {
  if (saving.value) return 'Enregistrement…'
  if (dirty.value) return 'Modifié'
  if (savedAt.value) return 'Enregistré'
  return ''
})

watch(content, () => { if (loaded.value) dirty.value = true })

async function load() {
  try {
    const r = await NotebookAPI.get()
    content.value = r.content || ''
    savedAt.value = r.updated_at
  } catch { content.value = '' }
  finally { loaded.value = true; dirty.value = false }
}

async function save() {
  if (!dirty.value) return
  saving.value = true
  try {
    const r = await NotebookAPI.save(content.value)
    savedAt.value = r.updated_at
    dirty.value = false
  } finally { saving.value = false }
}

async function onClose(v: boolean) {
  if (!v) await save()
  emit('update:modelValue', v)
}

watch(() => props.modelValue, (open) => { if (open) { loaded.value = false; load() } })
</script>

<style scoped>
.title { font-size: 16px; font-weight: 700; margin: 0 0 2px; }
.sub { font-size: 12px; color: var(--text-muted); margin: 0 0 12px; }
.pad {
  width: 100%; min-width: 440px; min-height: 300px; box-sizing: border-box;
  padding: 14px; border: 1px solid var(--border); border-radius: var(--radius);
  font-size: 14px; line-height: 1.6; font-family: var(--font, inherit); resize: vertical;
  background: var(--surface); color: var(--text);
}
.pad:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-100); }
.foot { display: flex; align-items: center; gap: 10px; margin-top: 12px; }
.status { font-size: 12px; color: var(--text-muted); }
.spacer { flex: 1; }
.btn-primary { padding: 8px 16px; background: var(--primary); color: #fff; border: none; border-radius: var(--radius); font-weight: 600; font-size: 13px; cursor: pointer; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
